/**
 * HighlightsPlayer — auto-playing reel of a player's boundary clips.
 *
 * Reads from /api/v1/players/<id>/highlights/?match_id= or vs_team=.
 * Renders each clip sequentially in a single <video>, advancing on
 * `onEnded`. Visually feels like one continuous highlights video,
 * which is the founder spec ("combine the clippings").
 *
 * Server-side concat (ffmpeg) is the "proper" way but each per-ball
 * MP4 is already in S3 — playlist-style playback is faster to ship,
 * cheaper to run, and gives the same UX without a worker pipeline.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, RotateCcw } from "lucide-react";

import apiAxios from "../../api/axios";

export default function HighlightsPlayer({ playerId, filter, title, onClose }) {
  // filter is { match_id?: string, vs_team?: string }
  const [clips, setClips] = useState(null);   // null=loading, []=empty, [...]=loaded
  const [error, setError] = useState("");
  const [idx, setIdx] = useState(0);
  // 2026-06-02 — track per-clip errors so we can skip past dead URLs
  // (S3 file 404, codec issue, network blip) without freezing the
  // reel. After we've tried every clip and they all fail, surface a
  // clean "no playable clips" state.
  const [failedIdx, setFailedIdx] = useState(new Set());
  // total_marked from API: how many boundary balls have a BallVideo
  // row marked but might lack a real S3 file. Used to explain the
  // "no playable clips" state.
  const [totalMarked, setTotalMarked] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setClips(null);
    setError("");
    setIdx(0);
    setFailedIdx(new Set());
    setTotalMarked(0);
    const params = new URLSearchParams();
    if (filter?.match_id) params.set("match_id", filter.match_id);
    if (filter?.vs_team)  params.set("vs_team", filter.vs_team);
    apiAxios
      .get(`/api/v1/players/${playerId}/highlights/?${params.toString()}`)
      .then((r) => {
        if (!alive) return;
        setClips(r.data?.clips || []);
        setTotalMarked(r.data?.total_marked || 0);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.response?.data?.detail || e?.message || "Couldn't load highlights.");
        setClips([]);
      });
    return () => { alive = false; };
  }, [playerId, filter?.match_id, filter?.vs_team]);

  // Autoplay each clip; advance when it ends.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const playPromise = v.play();
    // Modern browsers reject autoplay if the gesture chain breaks —
    // swallow the promise rejection so we don't log it as an error.
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }, [idx, clips]);

  // Close on Esc.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight" && clips && idx < clips.length - 1) setIdx((i) => i + 1);
      if (e.key === "ArrowLeft"  && idx > 0) setIdx((i) => i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clips, idx, onClose]);

  const current = clips && clips[idx];
  const total   = clips?.length || 0;
  const hasNext = clips && idx < total - 1;
  const hasPrev = idx > 0;
  const allFailed = !!clips && total > 0 && failedIdx.size >= total;

  const advanceToNextPlayable = () => {
    // Walk forward to the first non-failed index. If we run out,
    // mark this index as failed too — the parent render then
    // detects allFailed and shows the empty state.
    setFailedIdx((prev) => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    for (let i = idx + 1; i < total; i++) {
      if (!failedIdx.has(i)) {
        setIdx(i);
        return;
      }
    }
    // No more playable clips ahead. Stay put — render switches to
    // "no playable clips" message via allFailed.
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-bg-primary border border-bg-secondary rounded-xl shadow-2xl max-w-3xl w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-bg-secondary">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text-primary truncate">
              {title || "Boundary highlights"}
            </div>
            {clips && clips.length > 0 && current && (
              <div className="text-[11px] text-text-secondary mt-0.5">
                Clip {idx + 1} of {total}
                {totalMarked > total && (
                  <span className="ml-1 text-amber-300/80">
                    ({total} playable / {totalMarked} marked)
                  </span>
                )}
                {" "}·{" "}
                <span className={current.runs === 6 ? "text-purple-300" : "text-emerald-300"}>
                  {current.runs} runs
                </span>{" "}
                · Over {current.over}.{current.ball_in_over}
                {current.vs ? ` · vs ${current.vs}` : ""}
                {current.match_date ? ` · ${current.match_date}` : ""}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-bg-secondary text-text-secondary hover:text-text-primary shrink-0"
            aria-label="Close highlights"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Player */}
        <div className="bg-black aspect-video flex items-center justify-center">
          {clips === null && (
            <div className="text-text-secondary text-sm">Loading clips…</div>
          )}
          {clips && clips.length === 0 && (
            <div className="text-center px-6 py-12 max-w-lg">
              <p className="text-text-primary text-sm mb-2">
                {totalMarked > 0
                  ? "Clips were marked but no playable video files were uploaded."
                  : "No boundary clips yet for this selection."}
              </p>
              <p className="text-text-secondary text-[11px] leading-relaxed">
                {totalMarked > 0 ? (
                  <>
                    {totalMarked} boundary ball{totalMarked === 1 ? "" : "s"} have
                    timecodes saved (the operator marked when each ball started
                    and ended), but the actual MP4 files were never uploaded to
                    S3 for these matches. Two paths to fix:
                    {" "}
                    <span className="text-amber-200/90">
                      (1) upload the master match recording so the slicer can
                      produce per-ball clips, or (2) clip-upload each ball
                      directly during scoring.
                    </span>
                  </>
                ) : (
                  error || "Clips appear here once a scorer records boundary deliveries on video."
                )}
              </p>
              <p className="text-text-secondary text-[11px] mt-3 italic opacity-70">
                Tip: 390 boundary clips exist on prod. For a working
                example pick Sai Sudharsan in the Gujarat Titans vs CSK
                (2026-05-21) match — 52 playable boundaries. Shubman Gill
                and Jos Buttler both have 10 playable clips too.
              </p>
            </div>
          )}
          {clips && clips.length > 0 && allFailed && (
            <div className="text-center px-6 py-12 max-w-md">
              <p className="text-text-primary text-sm mb-2">
                None of the {total} clip{total === 1 ? "" : "s"} could be played.
              </p>
              <p className="text-text-secondary text-[11px] leading-relaxed">
                The S3 video files appear to be missing. The scorer marked
                these balls during coding, but the master recording was
                never uploaded / sliced. Once that's done, the reel will
                play correctly.
              </p>
            </div>
          )}
          {current && current.video_url && !allFailed && (
            <video
              ref={videoRef}
              key={current.ball_id}            /* force reload on change */
              src={current.video_url}
              autoPlay
              controls
              playsInline
              preload="auto"
              className="w-full h-full bg-black"
              onEnded={() => {
                if (hasNext) setIdx((i) => i + 1);
              }}
              onError={() => {
                // S3 404 / codec / network — flag this clip as
                // failed and walk forward to the next playable one.
                advanceToNextPlayable();
              }}
            />
          )}
        </div>

        {/* Footer — prev / next / replay */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-bg-secondary text-xs">
          <button
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            disabled={!hasPrev}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-bg-secondary text-text-primary hover:bg-bg-main disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button
            onClick={() => setIdx(0)}
            disabled={!clips || clips.length === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-bg-secondary text-text-primary hover:bg-bg-main disabled:opacity-40 disabled:cursor-not-allowed"
            title="Replay from start"
          >
            <RotateCcw className="w-4 h-4" />
            Restart
          </button>
          <button
            onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
            disabled={!hasNext}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-button-primary text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
